/**
 * Minting bearer secrets for CRM principals (API credentials).
 *
 * Lives in `lib/` rather than beside the API Access settings page because the
 * record editor also mints secrets — the Secrets association row on a credential
 * offers a custom create, the same treatment webhooks get. A `lib/` module must
 * never import from `pages/`, so the shared flow belongs here.
 */

import type { EventBuilder } from '@/lib/crm/core/EventBuilder';
import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import { createEventsClient } from '@/lib/crm/core/EventsClient';
import { apiPost } from '@/lib/http/client';
import { logger } from '@/lib/logger';

export interface GeneratedSecretResponse {
  token: string;
  hint: string;
  masked_hint: string;
}

/**
 * `format` selects the wire-format id the generic secrets endpoint mints
 * (`token` | `whsec` -- see `apps/secrets/views.py`'s `SECRET_FORMATS_BY_ID`).
 * Omitted defaults server-side to `token`.
 */
export async function generateSecret(
  orgId: number,
  format?: string
): Promise<GeneratedSecretResponse> {
  return apiPost<GeneratedSecretResponse>(
    `/api/orgs/${orgId}/secrets/`,
    format ? { format } : {}
  );
}

/**
 * Format-by-auth-method policy for webhook secret minting (both directions):
 * `bearer` mints an opaque url-safe token (wire-format id `token`); every
 * other method mints a Standard Webhooks-compatible secret (`whsec`).
 * `standard_webhooks` REQUIRES whsec (the verifier strips `whsec_` and
 * base64-decodes); `segment` treats the secret as an opaque raw string on
 * both sides, so whsec works there too and matches the historical policy.
 */
export function webhookAuthMethodToSecretFormat(
  authMethod: string | undefined
): 'token' | 'whsec' {
  return authMethod === 'bearer' ? 'token' : 'whsec';
}

/**
 * Dedup concurrent mint-and-attach calls for the same (org, entity) pair
 * (double-click, or a retry after a timeout whose request actually landed):
 * reuse the in-flight attempt -- and therefore the same minted secret --
 * rather than minting an extra vault row that `value_update_access: none`
 * could never let us correct afterward. Shared by `attachSecret` and
 * `mintEntitySecret` since a given entity id can only ever be one of the two
 * flows' principal/attach-point, so their key spaces never collide.
 */
const inFlightAttachments = new Map<string, Promise<AttachedSecret>>();

async function dedupInFlight(
  orgId: number,
  entityId: string,
  perform: () => Promise<AttachedSecret>
): Promise<AttachedSecret> {
  const key = `${orgId}:${entityId}`;
  const inFlight = inFlightAttachments.get(key);
  if (inFlight) {
    return inFlight;
  }

  const attempt = perform();
  inFlightAttachments.set(key, attempt);
  try {
    return await attempt;
  } finally {
    inFlightAttachments.delete(key);
  }
}

export interface AttachedSecret {
  secretEntityId: string;
  token: string;
  hint: string;
  masked_hint: string;
}

/**
 * Mint the bearer token FIRST, then create the `ahoy_secret` entity carrying
 * the returned `masked_hint` inline on `ahoy_secret_hint`, and associate it to
 * its principal.
 *
 * `ahoy_secret_hint` is caller-writable (`value_write_access: all`) but
 * write-once (`value_update_access: none`), so a user-sourced event can only
 * ever set it on the SAME create event that establishes the entity -- there is
 * no later step that could patch it in. Minting first guarantees the create
 * event always has a hint to attach.
 */
export async function attachSecret(
  orgId: number,
  userId: number,
  principalEntityId: string
): Promise<AttachedSecret> {
  return dedupInFlight(orgId, principalEntityId, () =>
    performAttachSecret(orgId, userId, principalEntityId)
  );
}

async function performAttachSecret(
  orgId: number,
  userId: number,
  principalEntityId: string
): Promise<AttachedSecret> {
  const { token, hint, masked_hint } = await generateSecret(orgId);

  const secretEntityId = crypto.randomUUID();
  const builder = createEventBuilder({
    sourceType: 'ahoy_user',
    sourceId: String(userId),
  });
  builder
    .objectCreated(secretEntityId, 'ahoy_secret', {
      ahoy_secret_hint: masked_hint,
    })
    .associationAdded(
      secretEntityId,
      'ahoy_secret',
      'ahoy_secret_to_principal',
      principalEntityId
    );

  try {
    await createEventsClient(orgId).submitEvents(builder.build());
  } catch (submitError) {
    logger.error(
      'Failed to attach minted secret to CRM entity',
      submitError instanceof Error
        ? submitError
        : new Error(String(submitError)),
      {
        component: 'crm_events',
        org_id: orgId,
        secret_entity_id: secretEntityId,
      }
    );
    throw new Error('Failed to attach the generated secret. Please try again.');
  }

  return { secretEntityId, token, hint, masked_hint };
}

export interface MintEntitySecretOptions {
  /** Entity type of the newly-minted secret, e.g. `ahoy_automation_webhook_secret`. */
  secretEntityType: string;
  /**
   * Association type written FROM `entityId` TO the new secret, e.g.
   * `ahoy_webhook_to_secret` for an inbound webhook or
   * `ahoy_outbound_webhook_to_secret` for an outbound one.
   */
  associationType: string;
  /** Wire-format id passed to `generateSecret` (`token` | `whsec`). */
  format?: string;
  /** Extra builder calls appended to the same submit batch (same seq run as
   *  the create + associate pair) -- e.g. re-enabling the entity in the same
   *  batch as a re-enable-after-breaker-trip mint. */
  extraEvents?: (builder: EventBuilder) => void;
}

/**
 * Mint a secret and attach it to `entityId` by writing the association FROM
 * `entityId` TO the new secret -- the opposite direction from `attachSecret`
 * (which writes secret -> principal for API credentials). Webhooks use this
 * direction because the webhook's own creator-scoped write grant is the
 * authorization gate; the freshly-minted secret entity has no creator grant
 * of its own to write an outgoing association from.
 *
 * Same accepted-risk posture as `attachSecret`: a mint whose CRM bind fails
 * leaves an orphaned Secret row -- acceptable, the hint is unreferenced and a
 * retry mints fresh. `dedupInFlight` bounds concurrent retries the same way.
 */
export async function mintEntitySecret(
  orgId: number,
  userId: number,
  entityId: string,
  entityTypeName: string,
  options: MintEntitySecretOptions
): Promise<AttachedSecret> {
  return dedupInFlight(orgId, entityId, () =>
    performMintEntitySecret(orgId, userId, entityId, entityTypeName, options)
  );
}

async function performMintEntitySecret(
  orgId: number,
  userId: number,
  entityId: string,
  entityTypeName: string,
  options: MintEntitySecretOptions
): Promise<AttachedSecret> {
  const { secretEntityType, associationType, format, extraEvents } = options;
  const { token, hint, masked_hint } = await generateSecret(orgId, format);

  const secretEntityId = crypto.randomUUID();
  const builder = createEventBuilder({
    sourceType: 'ahoy_user',
    sourceId: String(userId),
  });
  builder
    .objectCreated(secretEntityId, secretEntityType, {
      ahoy_secret_hint: masked_hint,
    })
    .associationAdded(
      entityId,
      entityTypeName,
      associationType,
      secretEntityId
    );
  extraEvents?.(builder);

  try {
    await createEventsClient(orgId).submitEvents(builder.build());
  } catch (submitError) {
    logger.error(
      'Failed to attach minted secret to CRM entity',
      submitError instanceof Error
        ? submitError
        : new Error(String(submitError)),
      {
        component: 'crm_events',
        org_id: orgId,
        secret_entity_id: secretEntityId,
      }
    );
    throw new Error('Failed to attach the generated secret. Please try again.');
  }

  return { secretEntityId, token, hint, masked_hint };
}
